import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  createPurchaseProofFormSchema,
  MAX_PURCHASE_PROOF_SIZE,
  type PurchaseProofFormValues,
  submitPurchaseProof
} from './purchase-proof-form-utils.ts';
import { getUserPurchaseProofsQueryKey } from '@r/api/generated/client.ts';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { Button } from '@ui/button.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

const purchaseProofAccept = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg']
};

/** Renders and submits the validated purchase-proof deposit form. */
export function PurchaseProofForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const schema = createPurchaseProofFormSchema({
    required: t('invoices.form.file.required'),
    invalidType: t('invoices.form.file.invalid.type'),
    tooLarge: t('invoices.form.file.too.large')
  });
  const form = useForm<PurchaseProofFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { comment: '' }
  });

  const submission = useMutation({
    mutationFn: submitPurchaseProof,
    onSuccess: () => {
      form.reset({ comment: '' });
      queryClient.invalidateQueries({ queryKey: getUserPurchaseProofsQueryKey() });
      toast.success(t('invoices.form.success'));
    },
    onError: (error) => {
      showOrvalErrorToast(error, t('invoices.form.error'));
    }
  });

  return (
    <Form {...form} schema={schema}>
      <form
        onSubmit={form.handleSubmit((values) => submission.mutate(values))}
        className="space-y-5"
      >
        <FormField
          control={form.control}
          name="file"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('invoices.form.file.label')}</FormLabel>
              <FormControl>
                <FileUploader
                  value={field.value ? [field.value] : []}
                  onValueChange={(nextValue) => {
                    const nextFiles =
                      typeof nextValue === 'function'
                        ? nextValue(field.value ? [field.value] : [])
                        : nextValue;
                    field.onChange(nextFiles[0]);
                  }}
                  accept={purchaseProofAccept}
                  maxFiles={1}
                  maxSize={MAX_PURCHASE_PROOF_SIZE}
                  disabled={submission.isPending}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">{t('invoices.form.file.help')}</p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="comment"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('invoices.form.comment.label')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={4}
                  className="resize-none bg-background"
                  disabled={submission.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" rounded="full" loading={submission.isPending}>
          <Upload aria-hidden />
          {t('invoices.form.submit')}
        </Button>
      </form>
    </Form>
  );
}
